import { SkillLevelScale } from "@/components/design/SkillLevelScale";
import { SkillPreviewSlot } from "@/components/skills/SkillPreviewSlot";
import { EscoBadge } from "@/components/esco/EscoBadge";
import { EscoEntryMapping } from "@/components/esco/EscoEntryMapping";
import { readClassification, type Classification } from "@/lib/esco";
import { Fragment, useState, type DragEvent, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SKILL_LEVELS, fromStoragePercent, toStoragePercent } from "@/lib/skill-levels";
import { skillLevelBandBackground, skillLevelSwatch } from "@/lib/skill-level-style";
import { useThemePrefs } from "@/stores/theme-prefs";
import { SkillLevelInfoIcon } from "@/components/design/SkillLevelInfoIcon";
import { NumberStepper } from "@/components/design/NumberStepper";
import { PlusButton } from "@/components/design/PlusButton";
import { RequirementPillLock } from "../RequirementPillLock";
import { requirementPillClasses } from "../requirement-pill-classes";
import { PersonIcon } from "../person-icon";
import { RequirementControls } from "./row-primitives";

// Bewerbungs-eigenes Fachkompetenz-Diagramm in der Optik des Profil-SkillsDiagram (save/):
// eine senkrechte Achse der 7 Stufen mit Level-Baendern, Skills als Chips auf ihrer Stufe,
// "Vorhanden" unten fuer Skills ohne Mindestlevel. Bewusst NICHT das Profil-Diagramm selbst — das haengt an EntryFields/
// SaveBar (@adr SPARK-0005-unified-deferred-save-bar@1) und Profil-Drafts. Muss/Kann steckt als Schloss am Chip (geschlossen =
// Muss, offen = Kann); Ziehen aendert nur die Stufe.

export type MatchSkillRequirement = "MUST" | "OPTIONAL";

export interface MatchSkillEntry {
  id: string;
  description: string;
  classification?: unknown;
  level: number; // 1..7, 0 = Vorhanden ohne Mindestlevel
  requirement: MatchSkillRequirement;
  // Nur im Team-Kontext gesetzt (Anzahl Personen, die den Skill mind. erfuellen muessen);
  // im Rank-Modus (1 Person) irrelevant und stets undefined.
  personCount?: number;
}

const UNRATED = 0;

function SkillTag({
  entry,
  active,
  dragging,
  readOnly,
  showLevels,
  onDragStart,
  onDragEnd,
  onClick,
  onToggleLock,
}: {
  entry: MatchSkillEntry;
  active: boolean;
  dragging: boolean;
  readOnly?: boolean;
  showLevels: boolean;
  onDragStart: (e: DragEvent) => void;
  onDragEnd: () => void;
  onClick: () => void;
  onToggleLock: () => void;
}) {
  const intl = useIntl();
  const isMust = entry.requirement === "MUST";
  const lockLabel = intl.formatMessage(
    isMust
      ? {
          id: "match.recruiting.must.tooltip",
          defaultMessage: "Muss-Kriterium: wer es nicht erfüllt, ist unqualifiziert. Klicken für Kann.",
        }
      : {
          id: "match.recruiting.kann.tooltip",
          defaultMessage: "Kann-Kriterium: zählt in den Passungs-Score. Klicken für Muss.",
        },
  );
  return (
    <span
      role={readOnly ? undefined : "button"}
      tabIndex={readOnly ? undefined : 0}
      aria-expanded={readOnly ? undefined : active}
      id={`match-skill-chip-${entry.id}`}
      data-testid="match-skill-chip"
      data-requirement={entry.requirement}
      title={entry.description}
      draggable={!readOnly && showLevels}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onKeyDown={(e) => {
        if (readOnly || e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`${requirementPillClasses({ isMust, editing: active })} ${
        readOnly ? "" : `${showLevels ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui`
      } ${dragging ? "opacity-40" : ""}`}
    >
      <RequirementPillLock isMust={isMust} label={lockLabel} disabled={readOnly}
        id={`match-skill-lock-${entry.id}`} onToggle={onToggleLock} />
      <span
        id={`match-skill-label-${entry.id}`}
        className="min-w-0 whitespace-normal break-words"
      >
        {entry.description || "–"}<EscoBadge value={entry.classification} text={entry.description} />
      </span>
      {/* Personenzahl-Badge nur im Team-Kontext (entry.personCount gesetzt); reine
          Anzeige, editiert wird im Panel darunter, nicht am Chip selbst. "+" dahinter
          liest sich als "mindestens/oder mehr" (wie "18+", "500+") - griffiger als das
          mathematische "≥" bei dieser Schriftgroesse. */}
      {typeof entry.personCount === "number" ? (
        <span className="inline-flex shrink-0 items-center gap-0.5 text-smycles-label tabular-nums opacity-80 [&>svg]:h-3 [&>svg]:w-3">
          <PersonIcon />
          {entry.personCount}+
        </span>
      ) : null}
    </span>
  );
}

type EditingActions = {
  onToggleRequirement: (id: string, requirement: MatchSkillRequirement) => void;
  onAdd: (description: string, level: number, requirement: MatchSkillRequirement, personCount?: number, classification?: Classification | null) => void;
  onRename: (id: string, description: string) => void;
  onDelete: (id: string) => void;
};

type MatchSkillsDiagramProps = {
  entries: MatchSkillEntry[];
  esco?: boolean;
  onClassificationChange?: (id: string, classification: Classification | null) => void;
  personCount?: boolean;
  showLevels?: boolean;
  placeholder?: string;
  onPlace?: (id: string, level: number) => void;
  onPersonCountChange?: (id: string, count: number) => void;
} & (
  | ({ readOnly?: boolean; renderEntry?: never } & EditingActions)
  | ({ readOnly: true; renderEntry?: (entry: MatchSkillEntry) => ReactNode } & Partial<EditingActions>)
);

export function MatchSkillsDiagram({
  entries,
  readOnly = false,
  personCount = false,
  showLevels = true,
  placeholder,
  onPlace,
  onToggleRequirement,
  onAdd,
  onRename,
  onDelete,
  onPersonCountChange,
  renderEntry,
  esco = false,
  onClassificationChange,
}: MatchSkillsDiagramProps) {
  const intl = useIntl();
  const accentColor = useThemePrefs((s) => s.accentColor);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverLevel, setHoverLevel] = useState<number | null>(null);
  const [dropLevel, setDropLevel] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState<
    { level: number; name: string; requirement: MatchSkillRequirement; personCount: number; classification?: Classification | null } | null
  >(null);
  const personCountLabel = intl.formatMessage({
    id: "match.recruiting.personCountLabel",
    defaultMessage: "Mindestanzahl Personen",
  });
  // Rundet/clampt vor dem Schreiben: NumberStepper laesst getippte Bruchwerte durch
  // (siehe NumberStepper-Doku), composition.value ist aber z.number().int() im
  // design-codec — ein Bruchwert liesse die Zeile sonst unbemerkt in
  // unknownConditions verschwinden.
  const clampPersonCount = (n: number) => Math.max(1, Math.round(n));

  const byLevel = new Map<number, MatchSkillEntry[]>();
  for (const e of entries) {
    const bucket = byLevel.get(e.level);
    if (bucket) bucket.push(e);
    else byLevel.set(e.level, [e]);
  }
  // Innerhalb eines Bandes: Muss-Chips zuerst (stabil), analog zur Row-Sortierung.
  for (const bucket of byLevel.values()) {
    bucket.sort((a, b) => Number(b.requirement === "MUST") - Number(a.requirement === "MUST"));
  }
  const unrated = byLevel.get(UNRATED) ?? [];
  const editingEntry = editingId ? entries.find((e) => e.id === editingId) ?? null : null;
  const previewName = !readOnly && showLevels ? (adding?.name ?? editingEntry?.description ?? "").trim() : "";
  const previewLevel = previewName ? (hoverLevel ?? adding?.level ?? null) : null;
  const previewAt = (level: number) => previewLevel === level ? previewName : null;
  const previewPercent = (percent: number | null) => setHoverLevel(percent === null ? null : fromStoragePercent(percent));

  const dropHandlers = (level: number) =>
    readOnly
      ? {}
      : {
          onDragOver: (e: DragEvent) => {
            if (!draggingId) return;
            e.preventDefault();
            if (dropLevel !== level) setDropLevel(level);
          },
          onDragLeave: () => setDropLevel((d) => (d === level ? null : d)),
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            const id = e.dataTransfer.getData("text/plain") || draggingId;
            if (id) onPlace?.(id, level);
            setDropLevel(null);
            setDraggingId(null);
          },
        };

  const tagProps = (e: MatchSkillEntry) => ({
    active: editingId === e.id,
    dragging: draggingId === e.id,
    readOnly,
    showLevels,
    onClick: () => {
      if (readOnly) return;
      setHoverLevel(null);
      setAdding(null);
      setEditingId((id) => (id === e.id ? null : e.id));
    },
    onToggleLock: () =>
      onToggleRequirement?.(e.id, e.requirement === "MUST" ? "OPTIONAL" : "MUST"),
    onDragStart: (ev: DragEvent) => {
      ev.dataTransfer.effectAllowed = "move";
      ev.dataTransfer.setData("text/plain", e.id);
      setDraggingId(e.id);
    },
    onDragEnd: () => {
      setDraggingId(null);
      setDropLevel(null);
    },
  });

  const renderTag = (entry: MatchSkillEntry) => (
    <Fragment key={entry.id}>
      {renderEntry ? renderEntry(entry) : <SkillTag entry={entry} {...tagProps(entry)} />}
    </Fragment>
  );

  function renderAddButton(level: number) {
    if (readOnly) return null;
    return (
      <button
        type="button"
        aria-label={intl.formatMessage({
          id: "match.recruiting.addSkillHere",
          defaultMessage: "Fachkompetenz hier hinzufügen",
        })}
        id={`match-skill-add-level-${level}`}
        data-testid="match-skill-add"
        onClick={() => {
          setHoverLevel(null);
          setEditingId(null);
          setAdding({ level, name: "", requirement: "OPTIONAL", personCount: 1 });
        }}
        className="shrink-0 rounded-full p-1 text-ink-muted opacity-0 transition hover:text-ink-accent focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ink-ui group-hover/level:opacity-100"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    );
  }

  // On its own card, like the profile's skills diagram: the level bands mix the accent into the card colour,
  // so everything on them must read against the card, not against the panel the diagram sits in.
  return (
    <div className="box bg-surface p-4">
      {showLevels ? (
        <div className="space-y-1">
          {[...SKILL_LEVELS].reverse().map((l) => {
            const swatch = skillLevelSwatch(l.value, accentColor);
            const levelName = intl.formatMessage({ id: l.nameId, defaultMessage: l.name });
            const skills = byLevel.get(l.value) ?? [];
            return (
              <div
                key={l.value}
                data-testid={`match-skill-level-${l.value}`}
                {...dropHandlers(l.value)}
                className={`group/level flex flex-wrap items-center gap-3 rounded-md px-2 py-2 transition ${
                  (dropLevel === l.value || previewLevel === l.value) ? "smycles-drop-target ring-2 ring-accent-decoration" : ""
                }`}
                style={{ background: skillLevelBandBackground(l.value) }}
              >
                <div className="flex w-40 shrink-0 items-center gap-2">
                  <span
                    className="flex h-7 w-7 items-center justify-center rounded-md text-smycles-label font-medium tabular-nums ring-1 ring-ink/10"
                    style={{ background: swatch.background, color: swatch.textColor }}
                  >
                    {l.value}
                  </span>
                  <span className="min-w-0 truncate text-smycles-meta text-ink">{levelName}</span>
                  <SkillLevelInfoIcon
                    level={l}
                    className="shrink-0 opacity-0 transition group-hover/level:opacity-100 group-focus-within/level:opacity-100"
                  />
                </div>
                <div className={`flex flex-1 items-center gap-3 ${skills.length ? "min-w-[min(100%,18rem)]" : "min-w-0"}`}>
                  <SkillPreviewSlot preview={previewAt(l.value)} className="min-h-7">
                    {skills.map(renderTag)}
                  </SkillPreviewSlot>
                  {renderAddButton(l.value)}
                </div>
              </div>
            );
          })}

          <div
            id="match-skill-level-unrated"
            data-testid="match-skill-level-unrated"
            {...dropHandlers(UNRATED)}
            className={`group/level mt-1 flex flex-wrap items-center gap-3 rounded-md px-2 py-2 transition ${
              (dropLevel === UNRATED || previewLevel === UNRATED) ? "smycles-drop-target ring-2 ring-accent-decoration" : ""
            }`}
          >
            <div className="w-40 shrink-0 text-smycles-meta text-ink-muted">
              {intl.formatMessage({ id: "match.recruiting.unrated", defaultMessage: "Vorhanden" })}
            </div>
            <div className={`flex flex-1 items-center gap-3 ${unrated.length ? "min-w-[min(100%,18rem)]" : "min-w-0"}`}>
              <SkillPreviewSlot preview={previewAt(UNRATED)} className="min-h-7">
                {unrated.map(renderTag)}
              </SkillPreviewSlot>
              {renderAddButton(UNRATED)}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="match-skill-pills">
            {[...entries].sort((a, b) => Number(b.requirement === "MUST") - Number(a.requirement === "MUST")).map(renderTag)}
          </div>
          {!readOnly && (
            <PlusButton
              label={intl.formatMessage({ id: "match.company.addExpertise", defaultMessage: "Fachkompetenz" })}
              onClick={() => {
                setHoverLevel(null);
                setEditingId(null);
                setAdding({ level: UNRATED, name: "", requirement: "OPTIONAL", personCount: 1 });
              }}
            />
          )}
        </div>
      )}

      {adding && !readOnly ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-page p-2 ring-1 ring-ink/10">
          <input
            type="text"
            autoFocus
            aria-label={intl.formatMessage({ id: "match.company.expertiseTitle", defaultMessage: "Fachkompetenz" })}
            id="match-skill-add-input"
            data-testid="match-skill-add-input"
            value={adding.name}
            onChange={(e) => setAdding((s) => (s ? { ...s, name: e.target.value } : s))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && adding.name.trim()) {
                if (esco) onAdd?.(adding.name.trim(), adding.level, adding.requirement, personCount ? adding.personCount : undefined, adding.classification);
                else onAdd?.(adding.name.trim(), adding.level, adding.requirement, personCount ? adding.personCount : undefined);
                setAdding(null);
              } else if (e.key === "Escape") {
                setAdding(null);
              }
            }}
            placeholder={placeholder ?? intl.formatMessage({ id: "match.recruiting.skillPlaceholder", defaultMessage: "z. B. Wundmanagement" })}
            className="h-8 min-w-0 flex-[1_1_16rem] rounded-md bg-surface px-2 text-smycles-meta text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
          />
          {esco ? <div className="basis-full"><EscoEntryMapping kind="skill" text={adding.name} value={adding.classification}
            onChange={value => setAdding(s => s ? { ...s, classification: readClassification(value) } : s)}
            onTextChange={name => setAdding(s => s ? { ...s, name } : s)} /></div> : null}
          {showLevels ? <div className="basis-full pt-2">
            <SkillLevelScale idPrefix="match-skill-add-stage" value={toStoragePercent(adding.level)} onPreview={previewPercent}
              onChange={percent => setAdding(s => s ? { ...s, level: fromStoragePercent(percent) } : s)} />
          </div> : null}
          {/* Convenience: Muss direkt beim Anlegen setzen (gleicher Toggle wie in den Rows). */}
          <RequirementControls
            requirement={adding.requirement}
            onChange={(req) => setAdding((s) => (s ? { ...s, requirement: req } : s))}
          />
          {personCount ? (
            <span className="flex shrink-0 items-center gap-1 text-ink [&>svg]:h-3.5 [&>svg]:w-3.5">
              <PersonIcon />
              <NumberStepper
                value={adding.personCount}
                onChange={(n) => setAdding((s) => (s ? { ...s, personCount: clampPersonCount(n) } : s))}
                min={1}
                ariaLabel={personCountLabel}
              />
            </span>
          ) : null}
          <button
            type="button"
            disabled={!adding.name.trim()}
            id="match-skill-add-confirm"
            data-testid="match-skill-add-confirm"
            onClick={() => {
              if (esco) onAdd?.(adding.name.trim(), adding.level, adding.requirement, personCount ? adding.personCount : undefined, adding.classification);
                else onAdd?.(adding.name.trim(), adding.level, adding.requirement, personCount ? adding.personCount : undefined);
              setAdding(null);
            }}
            className="shrink-0 rounded-md px-2 py-1 text-smycles-meta text-ink-accent disabled:opacity-40"
          >
            <FormattedMessage id="common.add" defaultMessage="Hinzufügen" />
          </button>
          <button
            type="button"
            onClick={() => setAdding(null)}
            className="shrink-0 rounded-md px-2 py-1 text-smycles-meta text-ink-muted hover:text-ink"
          >
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </button>
        </div>
      ) : null}

      {editingEntry && !readOnly ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-page p-2 ring-1 ring-ink/10">
          <input
            key={editingEntry.id}
            type="text"
            autoFocus
            aria-label={intl.formatMessage({ id: "match.company.expertiseTitle", defaultMessage: "Fachkompetenz" })}
            id="match-skill-edit-input"
            data-testid="match-skill-edit-input"
            value={editingEntry.description}
            onChange={(e) => onRename?.(editingEntry.id, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Escape") setEditingId(null);
            }}
            className="h-8 min-w-0 flex-[1_1_16rem] rounded-md bg-surface px-2 text-smycles-meta text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
          />
          {esco ? <div className="basis-full"><EscoEntryMapping kind="skill" text={editingEntry.description} value={editingEntry.classification}
            onChange={value => onClassificationChange?.(editingEntry.id, readClassification(value))}
            onTextChange={name => onRename?.(editingEntry.id, name)} /></div> : null}
          {showLevels && onPlace ? <div className="basis-full pt-2">
            <SkillLevelScale idPrefix="match-skill-edit-stage" value={toStoragePercent(editingEntry.level)} onPreview={previewPercent}
              onChange={percent => onPlace(editingEntry.id, fromStoragePercent(percent))} />
          </div> : null}
          {personCount ? (
            <span className="flex shrink-0 items-center gap-1 text-ink [&>svg]:h-3.5 [&>svg]:w-3.5">
              <PersonIcon />
              <NumberStepper
                value={editingEntry.personCount ?? 1}
                onChange={(n) => onPersonCountChange?.(editingEntry.id, clampPersonCount(n))}
                min={1}
                ariaLabel={personCountLabel}
              />
            </span>
          ) : null}
          <button
            type="button"
            id="match-skill-delete"
            data-testid="match-skill-delete"
            onClick={() => {
              onDelete?.(editingEntry.id);
              setEditingId(null);
            }}
            className="shrink-0 rounded-md px-2 py-1 text-smycles-meta text-ink-muted hover:text-ink"
          >
            <FormattedMessage id="common.remove" defaultMessage="Entfernen" />
          </button>
          <button
            type="button"
            onClick={() => setEditingId(null)}
            className="shrink-0 rounded-md px-2 py-1 text-smycles-meta text-ink-accent"
          >
            <FormattedMessage id="profile.rows.done" defaultMessage="Fertig" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
